import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import useOutsideClick from '../../hooks/useOutsideClick';
import './scss/SearchSelect.scss';

const SearchSelect = ({ options, isMultiple = false, onSelect, placeholder = 'Select an option', isGrouped = false, defaultValue, isDisabled = false }) => {
    // Ensure defaultValue is always an array
    const [searchTerm, setSearchTerm] = useState('');
    // Lazy initializer so we resolve the defaultValue (a value-id string) to
    // the matching option object up-front. Previously this stored the raw
    // string, then a useEffect upgraded it to an array on next tick — which
    // briefly let `selectedOptions[0].label` evaluate to undefined on first
    // paint.
    const [selectedOptions, setSelectedOptions] = useState(() => {
        if (!defaultValue) return null;
        const match = (options || []).find(o => o.value === defaultValue);
        return match ? [match] : null;
    });
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const selectRef = useRef();
    const dropdownRef = useRef();
    const selectedOptionRef = useRef(null);

    useOutsideClick(selectRef, () => {
        setIsOpen(false);
        setIsFocused(false);
    });

    useEffect(() => {
        setFilteredOptions(
            options && options.filter(option =>
                option.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, options]);

    // Keep a ref to the latest options so the sync effect below can read them
    // without depending on the array reference. Parents (e.g. CardCustomFields)
    // build a new options array on every render — including it in the deps
    // caused this effect to re-run after the user clicked an option but before
    // the parent's async commit landed, overwriting the local selection with
    // the still-stale defaultValue. First click looked like it didn't take;
    // second click worked because by then defaultValue had caught up.
    const optionsRef = useRef(options);
    useEffect(() => { optionsRef.current = options; }, [options]);

    useEffect(() => {
        if (defaultValue) {
            const selectedOption = optionsRef.current.find(option => option.value === defaultValue);
            if (selectedOption) {
                setSelectedOptions([selectedOption]);
            }
        }
    }, [defaultValue]);

    // Position the dropdown once per open. useLayoutEffect runs before paint
    // so the dropdown never flashes in the wrong position (which previously
    // looked like "the option open in top side it goes to bottom side").
    // Only depends on `isOpen` — refs don't drive re-runs, and re-running on
    // selectedOptionRef changes was what made the dropdown flip while clicking.
    useLayoutEffect(() => {
        if (!isOpen) return;
        const dropdown = dropdownRef.current;
        const trigger = selectRef.current;
        if (!dropdown || !trigger) return;

        // Find the nearest scrollable ancestor so we account for overflow:auto/
        // scroll containers (modals, panels) — not just the viewport.
        const getScrollableParent = (node) => {
            let el = node?.parentElement;
            while (el && el !== document.body) {
                const style = window.getComputedStyle(el);
                const overflowY = style.overflowY;
                if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
                    && el.scrollHeight > el.clientHeight) {
                    return el;
                }
                el = el.parentElement;
            }
            return null;
        };

        const triggerRect = trigger.getBoundingClientRect();
        const scrollParent = getScrollableParent(trigger);
        const parentRect = scrollParent ? scrollParent.getBoundingClientRect() : null;

        const boundaryTop = parentRect ? Math.max(0, parentRect.top) : 0;
        const boundaryBottom = parentRect
            ? Math.min(window.innerHeight, parentRect.bottom)
            : window.innerHeight;

        const spaceBelow = boundaryBottom - triggerRect.bottom;
        const spaceAbove = triggerRect.top - boundaryTop;
        const dropdownHeight = dropdown.offsetHeight;

        const openUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

        if (openUp) {
            dropdown.style.top = 'auto';
            dropdown.style.bottom = 'calc(100% - 14px)';
        } else {
            dropdown.style.top = 'calc(100% + 2px)';
            dropdown.style.bottom = 'auto';
        }

        // Bring the currently-selected option into view within the dropdown
        // by adjusting the dropdown's OWN scrollTop. The previous version used
        // Element.scrollIntoView, which walks up and scrolls every ancestor —
        // that shifted the trigger on screen mid-open and made the next click
        // land in the wrong place.
        const selectedOption = selectedOptionRef.current;
        if (selectedOption && dropdown.contains(selectedOption)) {
            const optionTop = selectedOption.offsetTop;
            const optionBottom = optionTop + selectedOption.offsetHeight;
            if (optionTop < dropdown.scrollTop) {
                dropdown.scrollTop = optionTop;
            } else if (optionBottom > dropdown.scrollTop + dropdown.clientHeight) {
                dropdown.scrollTop = optionBottom - dropdown.clientHeight;
            }
        }
    }, [isOpen]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Compare options by `value`, not by object reference. Parents rebuild
    // the `options` array on every render (e.g. CardCustomFields maps fresh
    // option objects each time), so reference equality goes stale after the
    // first click — that was the "first click doesn't take, second one does"
    // symptom.
    const isSameOption = (a, b) => a && b && a.value === b.value;

    const handleOptionClick = (option) => {
        setSearchTerm('');
        setIsOpen(false);

        if (isMultiple) {
            const current = Array.isArray(selectedOptions) ? selectedOptions : [];
            const next = current.some(o => isSameOption(o, option))
                ? current.filter(o => !isSameOption(o, option))
                : [...current, option];
            setSelectedOptions(next);
            onSelect(next);
        } else {
            setSelectedOptions([option]);
            onSelect(option);
        }
    };

    const handleInputBlur = () => {
        setIsFocused(false);
        // setTimeout(() => setIsOpen(false), 200); // Delay to allow click event on options
    };

    const selectedLabels = isMultiple
        ? (Array.isArray(selectedOptions) ? selectedOptions.map(option => option.label).join(', ') : '')
        : (selectedOptions && selectedOptions[0] ? selectedOptions[0].label : placeholder);

    const groupedOptions = isGrouped ? options.reduce((acc, option) => {
        if (!option.workspaceShortId) return acc;

        const workspaceGroup = acc.find(group => group.workspaceShortId === option.workspaceShortId);

        if (workspaceGroup) {
            workspaceGroup.boards.push(option);
        } else {
            acc.push({
                workspaceShortId: option.workspaceShortId,
                workspaceName: option.workspaceName,
                boards: [option]
            });
        }

        return acc;
    }, []) : [];

    const renderGroupedOptions = () => (
        <ul className="search-select-options" ref={dropdownRef}>
            {groupedOptions.map(group => (
                <li key={group.workspaceShortId} className="search-select-group">
                    <label className="search-select-group-label">
                        {group.workspaceName}
                    </label>
                    <ul className='search-select-option-list'>
                        {group.boards.map((option, index) => (
                            <li
                                key={index}
                                className={`search-select-option${(Array.isArray(selectedOptions) && selectedOptions.some(o => isSameOption(o, option))) ? ' selected' : ''}`}
                                // mousedown.preventDefault keeps focus on the
                                // input so the dropdown doesn't blur-close.
                                // We commit on click so the option element is
                                // still mounted when mouseup fires — otherwise
                                // the stray click can land on the trigger that
                                // was uncovered by the closing dropdown and
                                // reopen it below.
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOptionClick(option);
                                }}
                            >
                                <div className="selectable-option">
                                    <div dangerouslySetInnerHTML={{ __html: option.label }} />
                                </div>
                            </li>
                        ))}
                    </ul>
                </li>
            ))}
        </ul>
    );

    const renderNormalOptions = () => (
        <ul className="search-select-options" ref={dropdownRef}>
            {filteredOptions && filteredOptions.map((option, index) => {
                return (
                    <li
                        key={index}
                        className={`search-select-option${(Array.isArray(selectedOptions) && selectedOptions.some(o => isSameOption(o, option))) ? ' selected' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOptionClick(option);
                        }}
                        ref={(Array.isArray(selectedOptions) && selectedOptions.some(o => isSameOption(o, option))) ? selectedOptionRef : null}
                        data-item={index}
                    >
                        <div className="selectable-option">
                            <div dangerouslySetInnerHTML={{ __html: option.label }} />
                        </div>
                    </li>
                )
            })}
        </ul>
    );

    return (
        <div className={`search-select${isFocused ? ' focused' : ''}${isDisabled ? ' full-deactivated' : ''}`} ref={selectRef}>
            <div
                className="search-select-trigger"
                onClick={() => {
                    if (!isDisabled) {
                        setIsOpen(!isOpen);
                        setIsFocused(!isOpen);
                    }
                }}
            >
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onBlur={handleInputBlur}
                    readOnly={!isFocused}
                    placeholder={(selectedLabels ? selectedLabels.replace(/<br\s*\/?><span\s+className=["']current-option["']>\(current\)<\/span>/g, '') : placeholder)}

                />


                <span className="material-symbols-outlined">
                    keyboard_arrow_down
                </span>
                
                
            </div>
            {!isDisabled && isOpen && (isGrouped ? renderGroupedOptions() : renderNormalOptions())}
            {/* {isOpen || true && (isGrouped ? renderGroupedOptions() : renderNormalOptions())} */}
        </div>
    );
};

export default SearchSelect;
